import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import AdminNav from "../components/AdminNav";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sparkles,
  Trophy,
  Users,
  RotateCcw,
  Share2,
  Bike,
  History,
} from "lucide-react";
import { formatDatePt } from "../lib/format";

const HISTORY_KEY = "sf_raffle_last_round";
const SPIN_BASE_MS = 1500;
const SPIN_STAGGER_MS = 700;
const TICK_MS = 75;

function pickRandomWinners(pool, n) {
  const copy = [...pool];
  const winners = [];
  const count = Math.min(n, copy.length);
  for (let i = 0; i < count; i++) {
    const idx = Math.floor(Math.random() * copy.length);
    winners.push(copy.splice(idx, 1)[0]);
  }
  return winners;
}

function buildAnnouncement(rideName, winners) {
  const lines = [
    `🏁 OS GANHADORES DO SORTEIO FORAM:`,
    ``,
    ...winners.map((w, i) => `${i + 1}. ${w.rider_name}`),
    ``,
    `Rolê: ${rideName}`,
    `Parabéns a todos! A Speed Freaks agradece a sua participação!🏍️🔥`,
  ];
  return lines.join("\n");
}

export default function RafflePage() {
  const isAdmin = !!localStorage.getItem("sf_admin_token");
  const [rides, setRides] = useState([]);
  const [selectedRideId, setSelectedRideId] = useState("");
  const [rsvps, setRsvps] = useState([]);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [winnersCount, setWinnersCount] = useState(1);
  const [phase, setPhase] = useState("idle"); // idle | spinning | revealed
  const [displayed, setDisplayed] = useState([]);
  const [winners, setWinners] = useState([]);
  const [lastRound, setLastRound] = useState(null);
  const intervalsRef = useRef([]);

  // Load rides + last round from localStorage
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/rides")
      .then((res) => setRides(res.data || []))
      .catch(() => toast.error("Falha ao carregar rolês"));
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) setLastRound(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, [isAdmin]);

  // Load RSVPs when a ride is selected
  useEffect(() => {
    if (!selectedRideId) {
      setRsvps([]);
      return;
    }
    setRsvpLoading(true);
    api
      .get(`/rides/${selectedRideId}/rsvps`)
      .then((res) => setRsvps(res.data || []))
      .catch(() => {
        setRsvps([]);
        toast.error("Falha ao carregar confirmados");
      })
      .finally(() => setRsvpLoading(false));
  }, [selectedRideId]);

  // Cleanup any running interval on unmount / re-run
  useEffect(() => {
    return () => {
      intervalsRef.current.forEach((id) => clearInterval(id));
      intervalsRef.current = [];
    };
  }, []);

  const selectedRide = useMemo(
    () => rides.find((r) => r.id === selectedRideId),
    [rides, selectedRideId]
  );

  const canRaffle =
    !!selectedRideId &&
    rsvps.length > 0 &&
    winnersCount >= 1 &&
    winnersCount <= rsvps.length &&
    phase !== "spinning";

  const startRaffle = () => {
    if (!canRaffle) return;
    // Compute winners once
    const picked = pickRandomWinners(rsvps, winnersCount);
    setWinners(picked);
    setDisplayed(new Array(picked.length).fill(null));
    setPhase("spinning");

    // Clear any previous intervals
    intervalsRef.current.forEach((id) => clearInterval(id));
    intervalsRef.current = [];

    // Per-slot spinning
    picked.forEach((_, slotIndex) => {
      const duration = SPIN_BASE_MS + slotIndex * SPIN_STAGGER_MS;
      const iid = setInterval(() => {
        setDisplayed((prev) => {
          const next = [...prev];
          next[slotIndex] = rsvps[Math.floor(Math.random() * rsvps.length)];
          return next;
        });
      }, TICK_MS);
      intervalsRef.current.push(iid);

      // Stop this slot
      setTimeout(() => {
        clearInterval(iid);
        setDisplayed((prev) => {
          const next = [...prev];
          next[slotIndex] = picked[slotIndex];
          return next;
        });
        // Are we done?
        if (slotIndex === picked.length - 1) {
          const round = {
            ride_id: selectedRideId,
            ride_name: selectedRide?.name || "",
            ride_datetime: selectedRide?.ride_datetime || "",
            winners: picked,
            at: new Date().toISOString(),
          };
          setLastRound(round);
          try {
            localStorage.setItem(HISTORY_KEY, JSON.stringify(round));
          } catch {
            /* ignore quota */
          }
          setPhase("revealed");
        }
      }, duration);
    });
  };

  const resetRound = () => {
    intervalsRef.current.forEach((id) => clearInterval(id));
    intervalsRef.current = [];
    setPhase("idle");
    setWinners([]);
    setDisplayed([]);
  };

  const clearHistory = () => {
    if (!window.confirm("Limpar histórico da rodada anterior?")) return;
    localStorage.removeItem(HISTORY_KEY);
    setLastRound(null);
  };

  const shareWhatsApp = (rideName, winnersList) => {
    const text = buildAnnouncement(rideName, winnersList);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <AdminNav />

      <div className="mb-8">
        <div className="label-caps" style={{ color: "#00E5FF" }}>
          Painel · Ferramenta
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-white uppercase flex items-center gap-3">
          <Sparkles size={32} style={{ color: "#39FF14" }} className="glow-green" />
          Sorteador Speed Freaks
        </h1>
        <p className="text-white/60 text-sm mt-2">
          Escolha um rolê, quantos ganhadores e clica pra sortear entre os confirmados.
        </p>
      </div>

      {/* Config */}
      <div className="border border-white/10 bg-[#12141D] p-5 sm:p-6 space-y-5" data-testid="raffle-config">
        <div>
          <Label className="label-caps block mb-2">Rolê base do sorteio</Label>
          <Select value={selectedRideId} onValueChange={setSelectedRideId}>
            <SelectTrigger
              className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11 text-white"
              data-testid="raffle-ride-select"
            >
              <SelectValue placeholder="Selecione um rolê" />
            </SelectTrigger>
            <SelectContent className="bg-[#12141D] border-white/10 text-white">
              {rides.length === 0 ? (
                <div className="px-3 py-2 text-white/60 text-sm">Nenhum rolê cadastrado</div>
              ) : (
                rides.map((r) => (
                  <SelectItem
                    key={r.id}
                    value={r.id}
                    className="focus:bg-white/10 focus:text-white"
                    data-testid={`raffle-ride-option-${r.id}`}
                  >
                    {r.name} — {formatDatePt(r.ride_datetime)}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-caps block mb-2">Quantidade de ganhadores</Label>
            <Input
              type="number"
              min={1}
              max={Math.max(1, rsvps.length)}
              value={winnersCount}
              onChange={(e) => setWinnersCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
              data-testid="raffle-winners-input"
            />
          </div>
          <div>
            <Label className="label-caps block mb-2">Confirmados no rolê</Label>
            <div
              className="border border-white/10 bg-[#0B0D14] rounded-sm h-11 px-3 flex items-center gap-2 text-white"
              data-testid="raffle-pool-count"
            >
              <Users size={16} style={{ color: "#00E5FF" }} />
              {rsvpLoading ? (
                <span className="text-white/60 text-sm">Carregando...</span>
              ) : (
                <span className="font-semibold">{rsvps.length}</span>
              )}
              <span className="label-caps text-white/50">piloto(s)</span>
            </div>
          </div>
        </div>

        <Button
          onClick={startRaffle}
          disabled={!canRaffle}
          className="btn-primary w-full h-14 text-sm sm:text-base font-black tracking-widest"
          data-testid="raffle-start-btn"
          style={
            canRaffle
              ? { boxShadow: "0 0 28px rgba(57, 255, 20, 0.3)" }
              : undefined
          }
        >
          <Sparkles size={18} className="mr-2" /> REALIZAR SORTEIO
        </Button>
        {selectedRideId && !rsvpLoading && rsvps.length === 0 && (
          <div className="text-center label-caps text-white/50">
            Ninguém confirmou presença nesse rolê ainda
          </div>
        )}
      </div>

      {/* Result */}
      {(phase === "spinning" || phase === "revealed") && (
        <div
          className="mt-6 border border-white/10 bg-[#12141D] p-5 sm:p-6"
          data-testid="raffle-result"
        >
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={20} style={{ color: "#F59E0B" }} />
            <span className="label-caps" style={{ color: "#F59E0B" }}>
              {phase === "spinning" ? "Sorteando..." : "Ganhadores"}
            </span>
          </div>

          <div className="space-y-2" data-testid="raffle-winners-list">
            {displayed.map((entry, idx) => {
              const isFinal = phase === "revealed" || (entry && entry === winners[idx]);
              const isDone =
                phase === "revealed" ||
                (entry && winners[idx] && entry.id === winners[idx].id);
              return (
                <WinnerSlot
                  key={idx}
                  index={idx}
                  entry={entry}
                  done={isDone}
                  testid={`raffle-winner-${idx}`}
                />
              );
            })}
          </div>

          {phase === "revealed" && (
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={() =>
                  shareWhatsApp(selectedRide?.name || "Rolê", winners)
                }
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-sm bg-[#39FF14] text-black hover:brightness-110 transition-all"
                data-testid="raffle-announce-btn"
                style={{ boxShadow: "0 0 22px rgba(57, 255, 20, 0.4)" }}
              >
                <Share2 size={14} /> 📣 Anunciar Ganhadores no Grupo
              </button>
              <button
                onClick={resetRound}
                className="border border-white/15 hover:border-white px-4 py-2 text-xs uppercase tracking-widest text-white inline-flex items-center gap-2 transition-colors"
                data-testid="raffle-reset-btn"
              >
                <RotateCcw size={14} /> Nova Rodada
              </button>
            </div>
          )}
        </div>
      )}

      {/* Last round history */}
      {lastRound && phase !== "spinning" && phase !== "revealed" && (
        <div
          className="mt-6 border border-white/10 bg-[#0B0D14] p-5 sm:p-6"
          data-testid="raffle-history"
        >
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <History size={16} style={{ color: "#94A3B8" }} />
              <span className="label-caps">Rodada anterior</span>
            </div>
            <button
              onClick={clearHistory}
              className="label-caps text-white/40 hover:text-white transition-colors text-[10px]"
              data-testid="raffle-clear-history-btn"
            >
              Limpar
            </button>
          </div>
          <div className="text-white/70 text-sm mb-3">
            {lastRound.ride_name}
            {lastRound.ride_datetime && (
              <span className="text-white/40">
                {" "}— {formatDatePt(lastRound.ride_datetime)}
              </span>
            )}
          </div>
          <ol className="space-y-2" data-testid="raffle-history-winners">
            {lastRound.winners.map((w, idx) => (
              <li
                key={`${w.id}-${idx}`}
                className="flex items-center gap-3 border border-white/5 bg-[#12141D] px-3 py-2 rounded-sm"
              >
                <span className="font-display text-2xl text-[#00E5FF] w-8 text-center">
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold truncate">
                    {w.rider_name}
                  </div>
                  {w.motorcycle_model && (
                    <div className="text-white/50 text-xs flex items-center gap-1">
                      <Bike size={11} /> {w.motorcycle_model}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-3">
            <button
              onClick={() =>
                shareWhatsApp(lastRound.ride_name, lastRound.winners)
              }
              className="text-xs uppercase tracking-widest text-[#39FF14] hover:underline inline-flex items-center gap-1"
              data-testid="raffle-history-share-btn"
            >
              <Share2 size={12} /> Compartilhar essa rodada
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WinnerSlot({ index, entry, done, testid }) {
  return (
    <div
      className={`border px-4 py-3 rounded-sm flex items-center gap-3 transition-all duration-200 ${
        done
          ? "border-[#39FF14]/50 bg-[#39FF14]/5"
          : "border-white/10 bg-[#0B0D14]"
      }`}
      data-testid={testid}
      style={
        done ? { boxShadow: "inset 0 0 0 1px rgba(57,255,20,0.25)" } : undefined
      }
    >
      <span
        className={`font-display text-3xl w-10 text-center ${
          done ? "text-[#39FF14] glow-green" : "text-white/40"
        }`}
      >
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        {entry ? (
          <>
            <div
              className={`font-semibold truncate ${
                done ? "text-white text-lg" : "text-white/70 text-base"
              } ${!done ? "animate-pulse" : ""}`}
            >
              {entry.rider_name}
            </div>
            {done && entry.motorcycle_model && (
              <div className="text-white/60 text-xs flex items-center gap-1 mt-0.5">
                <Bike size={11} /> {entry.motorcycle_model}
                {entry.is_guest && (
                  <span
                    className="ml-2 label-caps text-[9px] px-1 py-0.5 border"
                    style={{
                      color: "#F59E0B",
                      borderColor: "rgba(245,158,11,0.35)",
                    }}
                  >
                    Convidado
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-white/40 text-sm">Aguardando...</div>
        )}
      </div>
      {done && <Trophy size={18} style={{ color: "#F59E0B" }} />}
    </div>
  );
}
